import { useMemo, useState } from 'react';
import { useCountriesQuery } from '@access-cms/api-client';

import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import { useBlurValidation } from '../../../../hooks/useBlurValidation';
import { PrimaryStep, SubStep, type WorkflowFormData, WorkflowSubStepProps } from '../../config';
import { required, requiredChoice } from '../../utils/fieldValidation';
import { useApplication, WorkflowAlertVariation } from '../../context';
import DocumentRemovalConfirmationModal from './DocumentRemovalConfirmationModal';
import {
  clearConditionalDocumentFields,
  type ConditionalDocumentField,
  deleteConditionalDocuments,
  getConditionalDocumentFileNames,
  getConditionalDocuments,
} from './citizenship-document-cleanup';

export interface ResidencyInformationFormData {
  isPermanentResident: boolean | null;
  citizenshipCountry: string;
  citizenshipCountryCode: string;
}

const yesNoOptions = [
  { label: '- Select an option -', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

function ResidencyInformationPage({
  data,
  updateData,
  fieldErrors,
}: WorkflowSubStepProps<ResidencyInformationFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingResidentStatus, setPendingResidentStatus] = useState<boolean | null | undefined>(undefined);
  const baseTestId = 'residency-information-page';
  const countriesQuery = useCountriesQuery({ excludeUs: false });
  const { applicationId, clearSubStepCompletions, formData, showWorkflowAlert, updateWorkflowData } = useApplication();

  const countries = useMemo(() => countriesQuery.data?.data ?? [], [countriesQuery.data?.data]);
  const countryOptions = useMemo(() => {
    const options = countries
      .filter((country) => country.countryCode && country.countryName)
      .map((country) => ({
        label: country.countryName as string,
        value: country.countryCode as string,
      }));
    const usOption = options.find((option) => option.value === 'US');

    return [
      { label: '- Start typing to filter results -', value: '' },
      ...(usOption ? [usOption] : []),
      ...options.filter((option) => option.value !== 'US'),
    ];
  }, [countries]);

  const {
    handleBlur,
    handleChange: handleValidationChange,
    getFieldError,
  } = useBlurValidation({
    externalErrors: fieldErrors,
    formData: data,
    rules: [
      {
        field: 'isPermanentResident',
        label: 'Are you a Permanent Resident of the United States (U.S.)?',
        validationRules: [requiredChoice],
      },
      { field: 'citizenshipCountry', label: 'Country of Citizenship', validationRules: [required] },
    ],
  });

  const updateResidencyStatus = (value: boolean | null, fieldsToClear: ConditionalDocumentField[]) => {
    updateWorkflowData((currentFormData: WorkflowFormData) => {
      let nextFormData = clearConditionalDocumentFields(currentFormData, fieldsToClear);
      const citizenshipDetails = nextFormData[PrimaryStep.CITIZENSHIP_DETAILS] ?? {};

      nextFormData = {
        ...nextFormData,
        [PrimaryStep.CITIZENSHIP_DETAILS]: {
          ...citizenshipDetails,
          [SubStep.RESIDENCY_INFORMATION]: {
            ...(citizenshipDetails[SubStep.RESIDENCY_INFORMATION] ?? data),
            isPermanentResident: value,
          },
          ...(value === true
            ? {
                [SubStep.IMMIGRATION_DOCUMENT]: {
                  documentType: 'Visa Number',
                  immigrationDate: '',
                  visaNumber: '',
                },
                [SubStep.US_ENTRY_INFORMATION]: {
                  entryVisaType: '',
                  firstEntryDate: '',
                  yearsInUS: '',
                },
              }
            : {}),
        },
      };

      return nextFormData;
    });
    clearSubStepCompletions([
      { stepId: PrimaryStep.CITIZENSHIP_DETAILS, subStepId: SubStep.US_ENTRY_INFORMATION },
      { stepId: PrimaryStep.CITIZENSHIP_DETAILS, subStepId: SubStep.IMMIGRATION_DOCUMENT },
      { stepId: PrimaryStep.DOCUMENT_UPLOAD, subStepId: SubStep.UPLOAD_DOCUMENTS },
    ]);
    handleValidationChange('isPermanentResident', value);
  };

  const handleChange = (field: keyof ResidencyInformationFormData, value: string | boolean | null) => {
    if (field === 'isPermanentResident' && value !== data.isPermanentResident) {
      const fieldsToClear: ConditionalDocumentField[] =
        value === true
          ? ['usVisaDocument', 'residencySupportingDocuments']
          : value === false
          ? ['residencyDocument', 'residencySupportingDocuments']
          : ['usVisaDocument', 'residencyDocument', 'residencySupportingDocuments'];
      const documents = getConditionalDocuments(formData, fieldsToClear);

      if (documents.length > 0) {
        setPendingResidentStatus(value as boolean | null);

        return;
      }

      updateResidencyStatus(value as boolean | null, fieldsToClear);

      return;
    }

    updateData({
      ...data,
      [field]: value,
    });
    handleValidationChange(field, value);
  };

  const handleConfirmStatusChange = async () => {
    if (pendingResidentStatus === undefined) return;
    const fieldsToClear: ConditionalDocumentField[] =
      pendingResidentStatus === true
        ? ['usVisaDocument', 'residencySupportingDocuments']
        : pendingResidentStatus === false
        ? ['residencyDocument', 'residencySupportingDocuments']
        : ['usVisaDocument', 'residencyDocument', 'residencySupportingDocuments'];
    const documents = getConditionalDocuments(formData, fieldsToClear);

    setIsDeleting(true);
    try {
      await deleteConditionalDocuments(applicationId, documents);
      updateResidencyStatus(pendingResidentStatus, fieldsToClear);
      setPendingResidentStatus(undefined);
    } catch {
      showWorkflowAlert({
        heading: 'Document Could Not Be Deleted',
        text: 'Your residency answer was not changed. Please try again.',
        variation: WorkflowAlertVariation.ERROR,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCountryChange = (countryCode: string) => {
    const selectedCountry = countries.find((country) => country.countryCode === countryCode);
    const citizenshipCountry = selectedCountry?.countryName ?? '';

    updateData({
      ...data,
      citizenshipCountry,
      citizenshipCountryCode: countryCode,
    });
    handleValidationChange('citizenshipCountry', citizenshipCountry);
  };

  const countryError =
    getFieldError('citizenshipCountry') ||
    (countriesQuery.isError ? 'Unable to load countries. Please refresh and try again.' : undefined);

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-permanent-resident`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-permanent-resident-dropdown`}
          errorMessage={getFieldError('isPermanentResident')}
          hint='Select whether you are a permanent resident of the United States'
          label='Are you a Permanent Resident of the United States (U.S.)?'
          name='isPermanentResident'
          options={yesNoOptions}
          tooltipText='A lawful permanent resident (green card holder) has been granted authorization to live and work in the United States on a permanent basis.'
          value={data.isPermanentResident ?? null}
          onBlur={() => handleBlur('isPermanentResident')}
          onChange={(value: boolean | null) => handleChange('isPermanentResident', value)}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-citizenship-country`}>
        <Dropdown
          required
          searchable
          data-testid={`${baseTestId}-citizenship-country-dropdown`}
          disabled={countriesQuery.isLoading || countriesQuery.isError}
          errorMessage={countryError}
          hint='Start typing to filter country results'
          label='Country of Citizenship'
          name='citizenshipCountry'
          options={countryOptions}
          tooltipText='The country where you hold citizenship. If you hold dual citizenship, select your primary citizenship country.'
          value={data.citizenshipCountryCode || ''}
          onBlur={() => handleBlur('citizenshipCountry')}
          onChange={handleCountryChange}
        />
      </div>

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Residency Information'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Residency Details</h3>
          <p>
            Please tell us about your residency status and citizenship. We will need these details to verify if you are
            eligible for employment.
          </p>

          <h4 className='ds-u-margin-top--3'>Are you a Permanent Resident of the United States (U.S.)?</h4>
          <p>
            Select "Yes" if you are a lawful permanent resident (green card holder). Select "No" if you are not a
            permanent resident but have other work authorization.
          </p>

          <h4 className='ds-u-margin-top--3'>Country of Citizenship</h4>
          <p>
            Select the country where you hold citizenship. If you hold dual citizenship, select your primary
            citizenship. Start typing to filter the list of countries.
          </p>
        </div>
      </Drawer>

      <DocumentRemovalConfirmationModal
        fileNames={getConditionalDocumentFileNames(
          formData,
          pendingResidentStatus === true
            ? ['usVisaDocument', 'residencySupportingDocuments']
            : pendingResidentStatus === false
            ? ['residencyDocument', 'residencySupportingDocuments']
            : ['usVisaDocument', 'residencyDocument', 'residencySupportingDocuments'],
        )}
        isDeleting={isDeleting}
        isOpen={pendingResidentStatus !== undefined}
        onCancel={() => setPendingResidentStatus(undefined)}
        onConfirm={() => void handleConfirmStatusChange()}
      />
    </div>
  );
}

export default ResidencyInformationPage;
